import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Download, FileText, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { products } from '../mock';
import { toast } from 'sonner';

const ProductDetail = () => {
  const { slug } = useParams();
  const product = products.find(p => p.slug === slug);

  useEffect(() => {
    if (product) {
      document.title = `${product.name} - Nova Global Supply`;
    }
  }, [product]);

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold mb-4">Product Not Found</h1>
        <Button asChild>
          <Link to="/">Return Home</Link>
        </Button>
      </div>
    );
  }

  const handleDownload = (doc) => {
    // If document has a URL, open it
    if (doc.url) {
      // Check if it's a backend API endpoint
      if (doc.url.startsWith('/api/')) {
        // Use full backend URL for API endpoints
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const fullUrl = `${BACKEND_URL}${doc.url}`;
        window.open(fullUrl, '_blank');
        toast.success("Opening Document", {
          description: `${doc.name} is opening in a new tab.`,
        });
      } else {
        // Direct external URL
        window.open(doc.url, '_blank');
        toast.success("Opening Document", {
          description: `${doc.name} is opening in a new tab.`,
        });
      }
    } else {
      // For documents without URLs (placeholders)
      toast.success("Download Ready", {
        description: `${doc.name} will be available after PDFs are uploaded.`,
      });
    }
  };

  return (
    <div className="flex flex-col">
      {/* Breadcrumb */}
      <div className="bg-slate-50 py-4 px-4">
        <div className="container mx-auto max-w-6xl">
          <Button asChild variant="ghost" size="sm">
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
        </div>
      </div>

      {/* Product Header */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Image */}
            <div className="aspect-square overflow-hidden rounded-lg">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Info */}
            <div>
              <Badge className="mb-4">{product.brand}</Badge>
              <h1 className="text-4xl font-bold text-slate-900 mb-4">{product.name}</h1>
              <p className="text-lg text-slate-600 mb-6">{product.fullDesc}</p>

              <div className="mb-6 p-4 bg-slate-50 rounded-lg border-2 border-slate-200">
                <p className="text-lg font-semibold text-slate-900">{product.pricing}</p>
              </div>

              <div className="space-y-4 mb-8">
                <h3 className="text-xl font-semibold text-slate-900">Key Features</h3>
                <ul className="space-y-2">
                  {product.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle2 className="h-5 w-5 text-slate-700 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* VersaDuct specific note about logo */}
              {product.slug === 'versaduct' && (
                <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-slate-700">
                    <strong>Identifying Authentic VersaDuct:</strong> Look for our VersaDuct logo printed on one side of the grey corrugated sheets. The logo can be installed facing visible or hidden based on your preference for finished or unfinished spaces.
                  </p>
                </div>
              )}

              <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white shadow-lg">
                <Link to="/contact">Request Pricing & Information</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Documents Section */}
      <section className="py-12 px-4 bg-slate-50">
        <div className="container mx-auto max-w-6xl">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Product Documentation</h2>
            <p className="text-slate-600">Download catalogs, specifications, and installation guides</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {product.documents.map((doc, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start space-x-3">
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-green-500 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{doc.name}</CardTitle>
                      <CardDescription className="text-sm">{doc.type} • {doc.size}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full border-blue-600 text-blue-600 hover:bg-blue-50"
                    onClick={() => handleDownload(doc)}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Download PDF
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* VersaFlex CSST specific - CSA Website Link */}
          {product.slug === 'versaflex-csst' && (
            <div className="mt-8 text-center">
              <Button
                asChild
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white shadow-lg"
              >
                <a 
                  href="https://www.csagroup.org/testing-certification/product-listing/certificate/?cert=259973-3335-04" 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  View Live CSA Listing
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
              <p className="text-sm text-slate-600 mt-3">
                Visit the official CSA Group website to verify certification details
              </p>
            </div>
          )}

          <div className="mt-8 p-6 bg-white rounded-lg border border-slate-200">
            <p className="text-sm text-slate-600">
              <strong>Note:</strong> Additional documents and technical specifications are available upon request.
              Contact our team for specific documentation needs or technical support.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Need More Information?
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Our team is ready to answer your questions and provide detailed product specifications
          </p>
          <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700 text-white shadow-lg">
            <Link to="/contact">Contact Us</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;